import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  Divider,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { useState, useCallback, useEffect, useRef, Dispatch } from 'react';
import { FormattedMessage } from 'react-intl';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import Chatbox from './chatbox';
import JoinMeetingModal from './joinMeetingModal';
import MessageArea from './messageArea';
import CollapsibleSchedule from './scheduledMeetings/collapsibleSchedule';

import VideoIcon from '../../assets/svg/videoCall.svg';
import useDecryption from '../../hooks/cryptography/decryptText';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';

import { FileMessageAttachment } from '~/interfaces/MessagesObject';
import { useTypedSelector } from '~/utils/store';

interface MessagesProps {
  conversationId: string;
  setList: Dispatch<React.SetStateAction<WebSocketMessage[]>>;
  setEnableDownload: (state: boolean) => void;
}

export interface WebSocketMessage {
  id: string;
  fromUser: number;
  toUser: number;
  message: string;
  createdAt: string;
  files: FileMessageAttachment[];
}

interface WebSocketMessageFromAPI {
  id: string;
  from_user: number;
  to_user: number;
  message: string;
  created_at: string;
  files: FileMessageAttachment[];
}

interface WSSendMessage {
  message: string;
  to_user?: number;
  action: string;
}

const styles = {
  buttonStyle: {
    textTransform: 'none',
    bgcolor: 'transparent',
    color: '#000000',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #D1D1D1',
    '&:hover': {
      bgcolor: '#D1D1D1',
      color: '#000000',
    },
    marginTop: '12px',
  },
};

const Messages = ({
  conversationId,
  setList,
  setEnableDownload,
}: MessagesProps) => {
  const [userId, setUserId] = useState(0);
  const WS_PATH = import.meta.env.VITE_WS_PATH;
  const LINK = '/report-chat';
  const CONVERSATION_PATH = `/${conversationId}`;
  const apiUrl = `${
    import.meta.env.VITE_WSS_PROTOCOL
  }${WS_PATH}${LINK}${CONVERSATION_PATH}/`;
  const SOCKET_URL = `${apiUrl}`;
  const [openMeetingModal, setOpenMeetingModal] = useState<boolean>(false);
  const [messageHistory, setMessageHistory] = useState<Array<WebSocketMessage>>(
    [],
  );
  const region = useTypedSelector((state) => state.auth.user.region);
  const [lastIdAdded, setLastIdAdded] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { createToastNotification } = useToastNotificationContext();
  const { decryptText } = useDecryption();

  const messagesEndRef = useRef<HTMLInputElement>(null);

  const { sendMessage, lastMessage, readyState } = useWebSocket(SOCKET_URL, {
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: () => true, // closeEvent
    queryParams: {
      token: `${accessToken}`,
      region: region || 'eu',
    },
  });

  //Extracts data from any cookie name available in the Cookie Document
  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Check if the cookie starts with the desired name
      if (cookie.indexOf(nameEQ) === 0) {
        // Get the value of the cookie
        const value = cookie.substring(nameEQ.length);
        return value;
      }
    }
    return null;
  };

  useEffect(() => {
    const wsAccessDev = localStorage.getItem('ws_access');
    if (wsAccessDev) {
      try {
        setAccessToken(wsAccessDev);
      } catch (error) {
        createToastNotification(
          'Could not establish connection for messages.',
          'error',
        );
      }
    } else {
      const extractedAccessToken = getCookie('ws_access_token');
      if (extractedAccessToken !== null) {
        try {
          setAccessToken(extractedAccessToken);
        } catch (error) {
          createToastNotification(
            'Could not establish connection for messages.',
            'error',
          );
        }
      }
    }
  }, [createToastNotification]);

  useEffect(() => {
    // Description in-transit decryption before displaying
    if (lastMessage !== null) {
      const websocketData = JSON.parse(lastMessage.data);
      if (
        websocketData.type === 'chat.history' &&
        websocketData.messages.length > 0 &&
        userId === 0
      ) {
        setUserId(parseInt(websocketData.user_id, 10));

        websocketData.messages.map(
          (
            {
              message,
              from_user: fromUser,
              to_user: toUser,
              created_at: createdAt,
              id,
              files,
            }: WebSocketMessageFromAPI,
            index: number,
          ) => {
            decryptText(message)
              .then((decryptedText) => {
                const data = {
                  id,
                  message: decryptedText,
                  fromUser,
                  toUser,
                  createdAt,
                  files,
                };
                setMessageHistory((prevState) => [...prevState, data]);
                setList((prevState) => [...prevState, data]);
              })
              .finally(() => {
                if (index === websocketData.messages.length - 1) {
                  setIsProcessing(false);
                }
              });
          },
        );
      }
      if (
        websocketData.type === 'chat.message' &&
        websocketData.id !== lastIdAdded
      ) {
        const {
          message,
          from_user: fromUser,
          to_user: toUser,
          created_at: createdAt,
          id,
          files,
        } = websocketData;

        decryptText(message).then((decryptedText) => {
          const data = {
            id,
            message: decryptedText,
            fromUser,
            toUser,
            createdAt,
            files,
          };
          setMessageHistory((prevState) => [...prevState, data]);
          setList((prevState) => [...prevState, data]);
        });

        setLastIdAdded(id); //save last message id to check wheather the message is already added
      }
      if (websocketData.messages?.length === 0) setIsProcessing(false);
    }
  }, [lastMessage, decryptText, setList, lastIdAdded, userId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isProcessing) scrollToBottom();
  }, [messageHistory, isProcessing]);

  useEffect(() => {
    if (readyState === 1) {
      setEnableDownload(true);
    }
  }, [readyState, setEnableDownload]);

  const clickSendMessageHandler = useCallback(
    (message: WSSendMessage) => {
      sendMessage(JSON.stringify(message));
    },
    [sendMessage],
  );

  const openMeetingModalHandler = () => {
    setOpenMeetingModal(true);
  };

  const closeMeetingModalHandler = () => {
    setOpenMeetingModal(false);
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: '0px 0px 4px 0px #0000000A',
          background: 'linear-gradient(0deg, #FFFFFF, #FFFFFF)',
          borderRadius: '12px',
          maxHeight: '100vh',
        }}
      >
        <Box
          sx={{
            padding: '1.8rem 0.75rem',
            backgroundColor: '#FAFAFA !important',
          }}
        >
          <Box sx={{ padding: '0rem 0.1rem 1rem 0.1rem' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '22px',
                  letterSpacing: '0rem',
                  textAlign: 'left',
                  color: '#000000',
                }}
              >
                <FormattedMessage defaultMessage='Message' />
              </Typography>
              <Button
                sx={styles.buttonStyle}
                startIcon={
                  <Box component='img' src={VideoIcon} alt='Video Call' />
                }
                onClick={openMeetingModalHandler}
              >
                <FormattedMessage defaultMessage='Join a meeting' />
              </Button>
            </Stack>

            <Box
              sx={{
                height: {
                  xs: '336px',
                  sm: '336px',
                  // lg: 'calc((100vh - 64px) - 510px)',
                },
                overflow: !isProcessing ? 'auto' : 'hidden',
              }}
            >
              {isProcessing && messageHistory.length >= 3 ? (
                messageHistory.map((message) => (
                  <Box key={message.id} sx={{ overflow: 'hidden' }}>
                    <Skeleton variant='text' width={100} height={20} />
                    <Skeleton variant='text' width={150} height={15} />
                    <Skeleton
                      variant='rectangular'
                      height={150}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ))
              ) : messageHistory.length !== 0 && isProcessing ? (
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <MessageArea
                  list={messageHistory}
                  userId={userId}
                  messagesEndRef={messagesEndRef}
                />
              )}

              {Array.isArray(messageHistory) && messageHistory.length === 0 && (
                <Box textAlign='center'>
                  <Typography sx={{ fontSize: 14 }}>
                    <FormattedMessage defaultMessage='No Message Found' />
                  </Typography>
                </Box>
              )}
            </Box>
            {!isProcessing && (
              <>
                <Divider sx={{ width: '100%' }} />
                <CollapsibleSchedule />
              </>
            )}
            <Chatbox
              disabled={readyState !== ReadyState.OPEN || isProcessing}
              onSendMessage={clickSendMessageHandler}
              conversationId={conversationId}
            />
          </Box>
        </Box>
      </Card>
      <JoinMeetingModal
        open={openMeetingModal}
        setClose={closeMeetingModalHandler}
      />
    </>
  );
};

export default Messages;
